/** @file dispatch: applies a command batch to the view at once, then commits it through the send queue. */
import { ids } from '@pose2d/format/ids';
import { throwError } from '@pose2d/format/throwError';
import type { CommandCall, Id, IdPrefix, Warning } from '@pose2d/format/types';
import { currentDoc } from '../../stores/currentDoc';
import { docStore } from '../../stores/docStore';
import { liveStore } from '../../stores/liveStore';
import { viewCache } from '../../stores/viewCache';
import type { ApplyResponse } from '../apiTypes';
import { liveSession } from './liveSession';
import { localApply } from './localApply';
import { sendBatch } from './sendBatch';

/** What a dispatch hands back at once, and the server's answer later. */
export interface DispatchHandle {
  /** name → id of what the optimistic apply created (`<kind>.create` ids are kept by the server). */
  created: Record<string, Id>;
  /** Warnings of the optimistic apply; the server's own come with `done`. */
  warnings: Warning[];
  /** The server's answer; rejects with its Pose2DError (which also becomes a notice in Activity). */
  done: Promise<ApplyResponse>;
}

const mint = (prefix: IdPrefix): Id => ids.create(prefix, Math.random, Date.now());

/** One batch = one commit = one undo step (E8-R3). Throws, queuing nothing, when no project is live or a command is
 *  refused locally; the view (`currentDoc`) shows the batch before this returns. */
export function dispatch(calls: CommandCall[]): DispatchHandle {
  const session = liveSession.get();
  const state = docStore.getState();
  const view = currentDoc(state);
  if (session === null || state.slice === null || view === null || state.projectId !== session.projectId) {
    return throwError('NOT_FOUND', { kind: 'project', ref: '(no project open)' });
  }
  const local = localApply(view, calls, session.actor, mint);
  const seq = session.nextSeq++;
  session.created.set(seq, local.created);
  const batches = [...liveStore.getState().batches, { seq, pending: local.pending, committedRev: null, sent: false }];
  const slice = { ...state.slice, pending: batches.flatMap((b) => b.pending) };
  viewCache.set(slice, local.doc);
  liveStore.setState({ batches });
  docStore.setState({ slice });
  return { created: local.created, warnings: local.warnings, done: sendBatch(session, seq) };
}
